import React from 'react';
import { defineMessage } from 'react-intl';
import { HAS_WINDOW } from '@eventbrite/legacy-infra.feature-detection';
import { ANCHOR_LINK } from '@eventbrite/legacy-eds.nav-list-item/constants';
import {
    GlobeChunky,
    InfoChunky,
    FlagChunky,
    UserChunky,
} from '@eventbrite/legacy-eds.iconography';
import { UserContent } from './user-content/UserContent';
import { Studio } from './studio/Studio';
import { getAnalyticsClickHandler } from '@eventbrite/legacy-infra.site-analytics';

import {
    ACTION_DISCOVER,
    ACTION_PROFILE_DROPDOWN,
    ACTION_LOGOUT,
    ACTION_GET_HELP,
    CATEGORY_HEADER,
    LABEL_ACCOUNT,
    LABEL_MY_EVENTS,
    LABEL_ORGANIZER_PROFILE,
    LABEL_SAVED,
    LABEL_TICKETS,
    LABEL_FOLLOWING,
    LABEL_COLLECTIONS,
    LABEL_INTERESTS,
    LABEL_SPONSORSHIPS,
    LABEL_STUDIO,
    LABEL_EMAIL_NAME_ACCOUNT,
    LABEL_GET_HELP,
} from '../constants/tracking';
import { Sponsorship } from './sponsorship/Sponsorship';

/**
 * @typedef {object} Config
 * @property {string} requestPath Current URL
 * @property {string} serverUrl Full base URL to the WWW site
 * @property {string} adminServerUrl Full base URL to the Admin site
 * @property {string} logoutUrl Path to the logout page
 * @property {string} accountSettingsPath Path to the user account settings page
 * @property {string} publicId Public user ID
 * @property {string} email User email
 * @property {string} fullName User full name
 * @property {string} userImageUrl Path to the user's image avatar
 * @property {object} helpUrls Help URLs
 * @property {string} helpUrls.home Home URL
 */
/**
 * @typedef {object} MenuItem
 * @property {'anchor' | 'routerLink'} type The type of link
 * @property {import('react').Element} content The content to display
 * @property {string} path The URL path
 * @property {function} [onClick] A function to call when the menu item is clicked (typically for analytics tracking)
 *
 */
/**
 * Gets a lookup of all the pre-configured user menu items given the specified data.
 * This can be used to build up an ordered list of user menu items in a global header.
 * @param {Config} config
 * @returns {Object.<string, MenuItem>}
 */

const logOutFunctions = (requestPath) => {
    // NOTE: We should clean the referal on the local storage to not put the user to a loop between where they come and the login
    if (HAS_WINDOW && localStorage) {
        localStorage.removeItem('referal');
    }

    getAnalyticsClickHandler({
        requestPath,
        action: ACTION_LOGOUT,
        category: CATEGORY_HEADER,
        label: LABEL_ACCOUNT,
    });
};

const getReferrerUrl = () => {
    if (HAS_WINDOW) {
        return window.location.href;
    }
    return '';
};

export const getUserMenuItemsLookup = ({
    // NOTE: If you add/change/remove arguments, you need to update all the callers
    requestPath,
    serverUrl,
    adminServerUrl,
    logoutUrl,
    accountSettingsPath,
    publicId,
    email,
    fullName,
    userImageUrl,
    isManageOverviewActive = false,
    isDetachedInterests = false,
    helpUrls,
}) => {
    const creatorUrl = () => {
        let url = isManageOverviewActive ? `${serverUrl}/manage/` : `${serverUrl}/myevents/`;

        // IMPORTANT: remove when New Creator Experience is over
        // https://eventbrite.atlassian.net/browse/EB-199459
        if (HAS_WINDOW) {
            const isCreatorHomeActivated = window.__SERVER_DATA__?.isCreatorHomeActivated;
            url = isCreatorHomeActivated ? `${serverUrl}/organizations/home` : url;
        }
        return url;
    };

    return {
        accountSettings: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Account settings', defaultMessage: 'Account settings' }),
            path: `${serverUrl}/${accountSettingsPath}/`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_ACCOUNT,
            }),
        },
        accountSettingsWithIcon: {
            type: ANCHOR_LINK,
            // NOTE: Even though `accountSettings` & `accountSettingsWithIcon` go to the same place,
            // we need their `path` property to be different because it's ultimately used as
            // the `key` when rendering the menu items. So this one below omits the trailing
            // slash to be different.
            path: `${serverUrl}/${accountSettingsPath}`,
            content: <UserContent fullName={fullName} email={email} />,
            name: 'user-menu-divider',
            'data-spec': 'user-menu-divider',
            imageUrl: userImageUrl,
            imageAlt: fullName,
            iconType: userImageUrl ? null : <UserChunky />,
            iconColor: 'grey-500',
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_EMAIL_NAME_ACCOUNT,
            }),
        },
        admin: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Admin', defaultMessage: 'Admin' }),
            path: `${adminServerUrl}/admin/search/`,
            iconType: <GlobeChunky />,
            target: '_blank',
            rel: 'noopener noreferrer',
        },
        attendeeNewsletter: {
            type: ANCHOR_LINK,
            content: defineMessage({
                id: 'Attendee newletter',
                defaultMessage: 'Attendee newletter',
            }),
            path: `${serverUrl}/hybrid_newsletter/`,
            target: '_blank',
            rel: 'noopener noreferrer',
        },
        browseEvents: {
            type: ANCHOR_LINK,
            path: `${serverUrl}/d/local/all-events/`,
            content: defineMessage({ id: 'Browse events', defaultMessage: 'Browse events' }),
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_DISCOVER,
                category: CATEGORY_HEADER,
            }),
        },
        collections: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Collections', defaultMessage: 'Collections' }),
            path: `${serverUrl}/u/${publicId}/favorites/collections`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_COLLECTIONS,
            }),
        },
        contacts: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Contacts', defaultMessage: 'Contacts' }),
            path: `${serverUrl}/contacts/`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_ORGANIZER_PROFILE,
            }),
        },
        create: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Create event', defaultMessage: 'Create event' }),
            path: `${serverUrl}/create/`,
        },
        featureFlags: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Feature Flags', defaultMessage: 'Feature Flags' }),
            path: `${adminServerUrl}/admin/nexus/gargoyle/`,
            iconType: <FlagChunky />,
            target: '_blank',
            rel: 'noopener noreferrer',
        },
        following: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Following', defaultMessage: 'Following' }),
            path: `${serverUrl}/u/${publicId}/followed/`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_FOLLOWING,
            }),
        },
        interests: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Interests', defaultMessage: 'Interests' }),
            path: isDetachedInterests
                ? `${serverUrl}/u/interests/tags/?mode=detached&referrer=${getReferrerUrl()}`
                : `${serverUrl}/u/interests`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_INTERESTS,
            }),
        },
        logout: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Log out', defaultMessage: 'Log out' }),
            path: logoutUrl,
            onClick: logOutFunctions.bind(null, requestPath),
        },
        manageEvents: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Manage my events', defaultMessage: 'Manage my events' }),
            path: creatorUrl(),
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_MY_EVENTS,
            }),
        },
        organizerProfile: {
            type: ANCHOR_LINK,
            content: defineMessage({
                id: 'Organizer profile',
                defaultMessage: 'Organizer profile',
            }),
            path: `${serverUrl}/myprofile/`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_ORGANIZER_PROFILE,
            }),
        },
        saved: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Liked', defaultMessage: 'Liked' }),
            path: `${serverUrl}/u/${publicId}/saved`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_SAVED,
            }),
        },
        sponsorships: {
            type: ANCHOR_LINK,
            content: <Sponsorship />,
            path: `${serverUrl}/sponsorship/connect/search/`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_SPONSORSHIPS,
            }),
        },
        studio: {
            type: ANCHOR_LINK,
            content: <Studio />,
            path: `${serverUrl}/studio/`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_STUDIO,
            }),
        },
        tickets: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Tickets', defaultMessage: 'Tickets' }),
            path: `${serverUrl}/u/${publicId}/`,
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_PROFILE_DROPDOWN,
                category: CATEGORY_HEADER,
                label: LABEL_TICKETS,
            }),
        },
        tikibar: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Tikibar', defaultMessage: 'Tikibar' }),
            path: '/tikibar/settings/',
            iconType: <InfoChunky />,
            target: '_blank',
            rel: 'noopener noreferrer',
        },
        help: {
            type: ANCHOR_LINK,
            content: defineMessage({ id: 'Get Help', defaultMessage: 'Get Help' }),
            path: helpUrls?.home || `${serverUrl}/support`,
            name: 'help-menu-item',
            'data-spec': 'help-menu-item',
            onClick: getAnalyticsClickHandler({
                requestPath,
                action: ACTION_GET_HELP,
                category: CATEGORY_HEADER,
                label: LABEL_GET_HELP,
            }),
        },
    };
};
